import { useCallback, useEffect, useMemo } from "react";

import { useGetCountryList } from "@vega-api/generated/country-v1/country-v1";
import { type CountryResponse } from "@vega-api/generated/types/countryResponse";

// This list contains countries for which ZIP code is optional
const ZIP_OPTIONAL_COUNTRIES = [
  "AE",
  "AG",
  "AO",
  "AQ",
  "AW",
  "BI",
  "BJ",
  "BO",
  "BS",
  "BV",
  "BW",
  "BZ",
  "CF",
  "CG",
  "CI",
  "CK",
  "CM",
  "CW",
  "DJ",
  "DM",
  "ER",
  "ET",
  "FJ",
  "GA",
  "GD",
  "GM",
  "GN",
  "GQ",
  "GW",
  "GY",
  "HK",
  "HN",
  "JM",
  "KM",
  "KN",
  "KP",
  "MO",
  "MR",
  "PA",
  "QA",
  "RW",
  "SB",
  "SC",
  "SD",
  "SL",
  "SO",
  "SR",
  "SS",
  "ST",
  "SX",
  "SY",
  "TD",
  "TF",
  "TG",
  "TK",
  "TL",
  "TO",
  "TV",
  "VU",
  "XZ",
  "YE",
  "ZM",
  "ZW",
];

export const useCountryCodes = () => {
  const { data: { data: countriesList = [] } = {} } = useGetCountryList({
    query: { refetchOnMount: false },
  });

  useEffect(() => {
    // Store zip optional country names in localStorage based on countriesList and use them in the validation schema
    if (countriesList.length > 0) {
      const existingZipOptionalCountryNames = localStorage.getItem(
        "zipOptionalCountryNames",
      );

      const existing = JSON.parse(existingZipOptionalCountryNames || "{}");

      if (Object.keys(existing).length === 0) {
        const zipOptionalCountryNames =
          getZipOptionalCountryNames(countriesList);

        localStorage.setItem(
          "zipOptionalCountryNames",
          JSON.stringify(zipOptionalCountryNames),
        );
      }
    }
  }, [countriesList]);

  const mapByCode = useMemo(
    () =>
      countriesList
        .toSorted((a, b) => {
          if (a.alpha2 === "US") return -1;

          if (b.alpha2 === "US") return 1;

          return 0;
        })
        .reduce(
          (acc, country) => {
            if (country.alpha2) {
              acc[country.alpha2] = country;
            }

            return acc;
          },
          {} as Partial<{ [key: string]: CountryResponse }>,
        ),
    [countriesList],
  );
  const mapByName = useMemo(
    () =>
      countriesList
        .toSorted((a, b) => {
          if (a.alpha2 === "US") return -1;

          if (b.alpha2 === "US") return 1;

          return 0;
        })
        .reduce(
          (acc, country) => {
            if (country.name) {
              acc[country.name] = country;
            }

            return acc;
          },
          {} as Partial<{ [key: string]: CountryResponse }>,
        ),
    [countriesList],
  );

  const getCountryCodeByName = useCallback(
    (name: string) => mapByName[name]?.alpha2 || name,
    [mapByName],
  );
  const getCountryNameByCode = useCallback(
    (code?: string) => (code ? mapByCode[code]?.name || code : ""),
    [mapByCode],
  );
  const getCountryByAlpha2 = useCallback(
    (alpha2: string) => mapByCode[alpha2],
    [mapByCode],
  );

  return {
    getCountryCodeByName,
    getCountryNameByCode,
    getCountryByAlpha2,
  };
};

function getZipOptionalCountryNames(countries: CountryResponse[]) {
  const result: Record<string, string | undefined> = {};
  for (const code of ZIP_OPTIONAL_COUNTRIES) {
    const country = countries.find((c: CountryResponse) => c.alpha2 === code);
    result[code] = country ? country.name : undefined;
  }

  return result;
}
